/**
 * Pro home page (design 3a): one loud Today band, two working panels
 * (My Calendar, Hot Bills), one quiet rail (changes + news). On desktop the
 * viewport is the frame — panels scroll internally and the page never grows;
 * below xl the panels cap their height and the page scrolls normally.
 *
 * A Session / Elections toggle swaps the band, the second panel, the rail
 * heading and the dateline — nothing else. The default is derived from the
 * legislative/election calendar; a manual override persists per user.
 */
import React, { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import api from '../lib/api';
import { usePageTitle } from '../hooks/usePageTitle';
import { ErrorIcon } from '../components/icons';
import { HelpTrigger } from '../help';
import TodayBand from '../components/dashboard/home/TodayBand';
import NewNoteButton from '../components/dashboard/home/NewNoteButton';
import NotificationsCard from '../components/dashboard/home/NotificationsCard';
import MyCalendarPanel from '../components/dashboard/home/MyCalendarPanel';
import HotBillsPanel from '../components/dashboard/home/HotBillsPanel';
import QuietRail from '../components/dashboard/home/QuietRail';
import { useHomeMode, type HomeMode } from '../components/dashboard/home/useHomeMode';
import {
  useElectionInfo,
  useHappeningNow,
  useHotBills,
  useMyCalendarEvents,
  useNewsFeed,
  useRecentChanges,
  useTrackedMeasures,
} from '../components/dashboard/home/useProHomeData';
import { eventsOnDay, toDateOnly } from '../components/dashboard/home/homeFormat';
import '../components/dashboard/home/home.css';

interface User {
  id: string;
  username: string;
  display_name: string;
  first_name: string;
  last_name: string;
  email: string;
  email_verified?: boolean;
}

function ModeToggle({ mode, onChange }: { mode: HomeMode; onChange: (m: HomeMode) => void }) {
  const options: Array<{ value: HomeMode; label: string }> = [
    { value: 'session', label: 'Session' },
    { value: 'elections', label: 'Elections' },
  ];
  return (
    <div
      role="tablist"
      aria-label="Dashboard mode"
      className="inline-flex rounded-[10px] border border-border-light dark:border-border-dark-hairline bg-neutral-100 dark:bg-background-dark-tertiary p-[3px]"
    >
      {options.map((option) => (
        <button
          key={option.value}
          role="tab"
          aria-selected={mode === option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={`rounded-lg px-3 py-1 text-[12.5px] font-semibold transition-colors ${
            mode === option.value
              ? 'bg-white dark:bg-background-dark-secondary text-primary-800 dark:text-text-dark-primary shadow-sm'
              : 'text-slate-500 dark:text-text-dark-secondary hover:text-slate-700'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

const SimpleDashboardPage: React.FC = () => {
  const queryClient = useQueryClient();

  const { data: meData } = useQuery(
    ['authMe'],
    async () => {
      const response = await api.get('/auth/me');
      return response.data?.data?.user;
    },
    { staleTime: 5 * 60 * 1000, retry: 1 },
  );
  const user = meData as User | null;

  const sendVerificationMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/auth/send-verification');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['authMe'] });
    },
  });

  const calendarQuery = useMyCalendarEvents();
  const billsQuery = useHotBills();
  const changesQuery = useRecentChanges();
  const newsQuery = useNewsFeed();
  const happeningNowQuery = useHappeningNow();
  const electionQuery = useElectionInfo();

  const eventsData = calendarQuery.data?.events;
  const events = useMemo(() => eventsData ?? [], [eventsData]);
  const todayKey = format(new Date(), 'yyyy-MM-dd');
  const todayEvents = useMemo(() => eventsOnDay(events, todayKey), [events, todayKey]);
  const upcomingEvents = useMemo(
    () => events.filter((e) => toDateOnly(e.reading_date) > todayKey),
    [events, todayKey],
  );

  // In session ⇔ regular-session events exist in the next two weeks.
  const hasSessionEventsSoon = useMemo(
    () => events.some((e) => e.meeting_type !== 'interim'),
    [events],
  );
  const { mode, setMode } = useHomeMode(
    hasSessionEventsSoon,
    electionQuery.data?.generalDate ?? null,
  );

  const measuresQuery = useTrackedMeasures(mode === 'elections');

  const trackedBillNumbers = useMemo(() => {
    const numbers = new Set<string>();
    for (const bill of billsQuery.data?.hotBills ?? []) numbers.add(bill.billNumber);
    for (const change of changesQuery.data?.changes ?? []) numbers.add(change.billNumber);
    return Array.from(numbers);
  }, [billsQuery.data, changesQuery.data]);

  const firstName = user?.first_name || user?.display_name || user?.username || '';

  const dateline = useMemo(() => {
    const dateLabel = format(new Date(), 'EEEE, MMMM d');
    const generalDate = electionQuery.data?.generalDate;
    if (mode === 'elections' && generalDate) {
      const days = differenceInCalendarDays(parseISO(generalDate), new Date());
      if (days >= 0) return `${dateLabel} · ${days} days to the general`;
    }
    return dateLabel;
  }, [mode, electionQuery.data]);

  usePageTitle(firstName ? `Welcome back, ${firstName}` : 'Welcome back', dateline);

  return (
    <div className="flex flex-col gap-4 xl:h-[calc(100vh-var(--desktop-header-height)-1.5rem)] xl:min-h-0 px-4 lg:px-0 pt-2 lg:pt-0">
      {/* Email verification prompt — only when not verified */}
      {user && user.email_verified === false && (
        <div className="card flex-none py-4">
          <div className="px-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-start gap-3">
              <ErrorIcon className="h-5 w-5 text-warning-500 mt-0.5" aria-hidden="true" />
              <div>
                <p className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                  Verify your email address
                </p>
                <p className="text-xs text-text-secondary dark:text-text-dark-secondary mt-1">
                  Please verify your primary email address so we can send you important account
                  updates and notifications.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => sendVerificationMutation.mutate()}
              disabled={sendVerificationMutation.isLoading}
              className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md bg-primary-700 text-white hover:bg-primary-800 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {sendVerificationMutation.isLoading ? 'Sending…' : 'Send verification email'}
            </button>
          </div>
        </div>
      )}

      {/* Mode toggle + New note — right-aligned, above the band */}
      <div className="flex-none flex items-center justify-end gap-2.5">
        <HelpTrigger contentKey="pro-home" size="sm" />
        <ModeToggle mode={mode} onChange={setMode} />
        <NewNoteButton />
      </div>

      <div className="flex-1 min-h-0 grid gap-4 grid-cols-1 xl:grid-cols-[minmax(0,1fr)_300px]">
        {/* Left: the Today band with the working panels directly beneath it */}
        <div className="flex flex-col gap-4 min-h-0">
          <TodayBand
            mode={mode}
            todayEvents={todayEvents}
            upcomingEvents={upcomingEvents}
            happeningNow={happeningNowQuery.data ?? null}
            electionInfo={electionQuery.data ?? null}
            measureCount={mode === 'elections' ? (measuresQuery.data?.length ?? null) : null}
          />
          <div className="flex-1 min-h-0 grid gap-4 grid-cols-1 lg:grid-cols-2">
            <MyCalendarPanel mode={mode} />
            <HotBillsPanel
              mode={mode}
              bills={billsQuery.data}
              billsLoading={billsQuery.isLoading}
              measures={measuresQuery.data}
              measuresLoading={measuresQuery.isLoading}
            />
          </div>
        </div>

        {/* Right: notifications card over the quiet news rail */}
        <div className="flex flex-col gap-4 min-h-0">
          <div className="flex-none min-h-0 max-h-[360px] flex flex-col">
            <NotificationsCard />
          </div>
          <div className="flex-1 min-h-0 max-h-[400px] xl:max-h-none overflow-hidden">
            <QuietRail news={newsQuery.data ?? []} trackedBillNumbers={trackedBillNumbers} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleDashboardPage;
